/*
 * MISS BULLITT — photo lightbox
 * Theme asset: assets/mb-lightbox.js
 *
 * Click a story photo and it opens centred and large; X in the corner, click
 * the backdrop, or press Escape to put it back. Desktop only — on a phone the
 * photos are already a full-width draggable rail, so a lightbox would be
 * covering one picture with the same picture.
 *
 * Three decisions worth knowing:
 *
 * 1. ONE delegated listener on the document, not a listener per photo. Sections
 *    are added, removed and re-rendered constantly in the theme customizer, and
 *    the mobile rail physically moves photo nodes in and out of a track. Any
 *    per-node binding has to be torn down and rebuilt on every one of those
 *    events; delegation simply never notices.
 *
 * 2. The overlay is built once and reused, and it lives at the end of <body> —
 *    NOT inside the section. Inside the section it would be trapped by the
 *    story beat's own stacking context and could end up under the riders.
 *
 * 3. `data-full` carries a larger Shopify image URL than the one in the grid.
 *    Without it the lightbox would upscale the thumbnail the browser already
 *    picked for a half-column slot, which looks worse enlarged than the
 *    original did small.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     RUN ONCE, however many times this file is loaded.

     The <script> tag lives in mb-story.liquid, so a page with five story
     beats emits five copies of it. The browser caches the FILE but still
     executes every <script> element — five times through this code means
     five overlays stacked on top of each other and five document listeners.
     Every click then closes only the topmost overlay and reveals the next,
     which reads as "I have to click the X several times".

     A module-level flag on window is the whole fix. It also covers the
     customizer re-injecting the section, which re-runs the script again.
     --------------------------------------------------------------------- */
  if (window.__mbLightboxLoaded) return;
  window.__mbLightboxLoaded = true;

  var BREAKPOINT = 749;
  var overlay = null, figure = null, img = null, cap = null;
  var lastFocus = null;

  function desktop() { return window.innerWidth > BREAKPOINT; }

  function build() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'mb-lb';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.hidden = true;
    overlay.innerHTML =
      '<figure class="mb-lb__fig">' +
        '<img class="mb-lb__img" alt="">' +
        '<figcaption class="mb-lb__cap"></figcaption>' +
        '<button type="button" class="mb-lb__close" aria-label="Luk">' +
          '<span aria-hidden="true">×</span>' +
        '</button>' +
      '</figure>';
    document.body.appendChild(overlay);
    figure = overlay.querySelector('.mb-lb__fig');
    img    = overlay.querySelector('.mb-lb__img');
    cap    = overlay.querySelector('.mb-lb__cap');

    overlay.addEventListener('click', function (e) {
      // Anything that is not the picture itself closes it: the backdrop, the
      // margin around the figure, and the X.
      if (e.target === img) return;
      close();
    });
  }

  function open(fig) {
    var src = fig.querySelector('img');
    if (!src) return;                     // a placeholder, nothing to enlarge
    build();

    var full = src.getAttribute('data-full') || src.currentSrc || src.src;
    var text = fig.querySelector('figcaption');

    img.src = full;
    img.alt = src.alt || '';
    if (text && text.textContent.trim()) {
      cap.textContent = text.textContent.trim();
      cap.hidden = false;
    } else {
      cap.textContent = '';
      cap.hidden = true;
    }

    lastFocus = document.activeElement;
    overlay.hidden = false;
    document.documentElement.classList.add('mb-lb-open');
    // one frame before the class, so the fade actually has a start state
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });
    overlay.querySelector('.mb-lb__close').focus();
  }

  function close() {
    if (!overlay || overlay.hidden) return;
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('mb-lb-open');
    var done = function () {
      overlay.hidden = true;
      img.removeAttribute('src');
      overlay.removeEventListener('transitionend', done);
    };
    overlay.addEventListener('transitionend', done);
    setTimeout(done, 300);                // in case the transition never fires
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  function candidate(el) {
    var fig = el.closest ? el.closest('.mb-photo') : null;
    if (!fig) return null;
    if (!fig.classList.contains('mb-photo--zoom')) return null;
    if (fig.closest('.mb-gallery')) return null;   // the mobile rail
    return fig;
  }

  document.addEventListener('click', function (e) {
    if (!desktop()) return;
    if (overlay && !overlay.hidden) return;
    var fig = candidate(e.target);
    if (!fig) return;
    e.preventDefault();
    open(fig);
  });

  // Keyboard: the figure carries tabindex/role from the section when zoom is on.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    if (!desktop()) return;
    var fig = candidate(e.target);
    if (!fig) return;
    e.preventDefault();
    open(fig);
  });

  // Rotating to a phone while it is open would leave a lightbox over a layout
  // that never offers one.
  window.addEventListener('resize', function () { if (!desktop()) close(); });

  document.addEventListener('shopify:section:unload', close);
})();
